import { useCallback, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { Input, Button, Select, Row, Col, Space, DatePicker } from 'antd';
import DateRangeFilter from './DateRangeFilter';
import styles from './styles/AdvancedTable.module.css';

const { Option } = Select;

const TableFilters = memo(({
  filterConfig = [],
  filters = {},
  onChange,
  onReset,
  onApply,
  errors = {},
}) => {
  const renderFilter = useCallback(config => {
    const value = filters[config.id];
    const error = errors[config.id];

    // Create stable onChange handlers
    const handleSelectChange = useCallback((val) => {
      onChange(config.id, val);
    }, [config.id, onChange]);

    const handleTextChange = useCallback((e) => {
      onChange(config.id, e.target.value);
    }, [config.id, onChange]);

    const handleDateRangeChange = useCallback((newValue) => {
      onChange(config.id, newValue);
    }, [config.id, onChange]);

    const handleDateChange = useCallback((date) => {
      onChange(config.id, date);
    }, [config.id, onChange]);

    const handleNumberChange = useCallback((field, e) => {
      onChange(config.id, {
        ...value,
        [field]: e.target.value,
      });
    }, [config.id, value, onChange]);

    switch (config.type) {
      case 'text':
        return (
          <Input
            placeholder={config.placeholder || `Enter ${config.label}`}
            value={value || ''}
            onChange={handleTextChange}
            status={error ? 'error' : ''}
          />
        );

      case 'select': {
        // Memoize options to prevent re-rendering
        const selectOptions = useMemo(() => 
          config.options?.map(option => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          )) || [], 
          [config.options]
        );

        return (
          <Select
            mode={config.multiple ? 'multiple' : undefined}
            placeholder={config.placeholder || `Select ${config.label}`}
            value={value}
            onChange={handleSelectChange}
            allowClear
            showSearch={config.searchable !== false}
            style={{ width: '100%' }}
            status={error ? 'error' : ''}
            dropdownStyle={{ zIndex: 9999 }}
            getPopupContainer={trigger => trigger.parentElement}
          >
            {selectOptions}
          </Select>
        );
      }

      case 'dateRange':
        return (
          <DateRangeFilter
            value={value}
            onChange={handleDateRangeChange}
            error={error}
            quickFilters={config.quickFilters || []}
            placeholder={config.placeholder}
            format={config.format}
            showTime={config.showTime}
            disabled={config.disabled}
            allowClear={config.allowClear}
          />
        );

      case 'date':
        return (
          <DatePicker
            value={value}
            onChange={handleDateChange}
            format={config.format || 'DD-MM-YYYY'}
            placeholder={config.placeholder || `Select ${config.label}`}
            style={{ width: '100%' }}
            status={error ? 'error' : ''}
            allowClear={config.allowClear !== false}
            disabled={config.disabled}
            getPopupContainer={trigger => trigger.parentElement}
          />
        );

      case 'numberRange':
        return (
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Min"
              type="number"
              value={value?.min || ''}
              onChange={(e) => handleNumberChange('min', e)}
              status={error ? 'error' : ''}
            />
            <Input
              placeholder="Max"
              type="number"
              value={value?.max || ''}
              onChange={(e) => handleNumberChange('max', e)}
              status={error ? 'error' : ''}
            />
          </Space.Compact>
        );

      default:
        return null;
    }
  }, [filters, errors, onChange]);

  return (
    <div className={styles.filterPanel}>
      <Row gutter={[16, 16]}>
        {filterConfig.map(config => (
          <Col key={config.id} xs={24} sm={12} md={8} lg={6}>
            <div className={styles.filterItem}>
              <label className={styles.filterLabel}>
                {config.label}
                {config.required && <span style={{ color: 'red' }}> *</span>}
              </label>
              <div className={styles.filterControl}>
                {renderFilter(config)}
              </div>
              {errors[config.id] && (
                <div className={styles.filterError}>{errors[config.id]}</div>
              )}
            </div>
          </Col>
        ))}
      </Row>

      <div className={styles.filterActions}>
        <Button onClick={onReset}>Reset Filters</Button>
        {onApply && (
          <Button type="primary" onClick={onApply}>
            Apply Filters
          </Button>
        )}
      </div>
    </div>
  );
});

TableFilters.displayName = 'TableFilters';

TableFilters.propTypes = {
  filterConfig: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['text', 'select', 'dateRange', 'date', 'numberRange']).isRequired,
      placeholder: PropTypes.string,
      options: PropTypes.arrayOf(
        PropTypes.shape({
          value: PropTypes.any.isRequired,
          label: PropTypes.string.isRequired,
        })
      ),
      multiple: PropTypes.bool,
      searchable: PropTypes.bool,
      quickFilters: PropTypes.array,
      required: PropTypes.bool,
    })
  ),
  filters: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  onApply: PropTypes.func,
  errors: PropTypes.object,
};

export default TableFilters;
